// src/pages/bloomterminal/Analytics.jsx
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, useOutletContext, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { useCommodities } from '../utils/api-hooks/commodity';
import NestedCommoditySelector from './components/NestedCommoditySelector';
import DateDropdown from './components/DateDropdown';
import {
  FILTER_CHEVRON_CLASS,
  FILTER_CHEVRON_SIZE,
  FILTER_LABEL_CLASS,
  FILTER_VALUE_CLASS,
  FILTER_VALUE_TEXT_CLASS,
  filterTriggerClass,
} from './components/filterControlStyles';
import { Icon } from '@iconify/react';
import { ChevronDown, RotateCcw } from 'lucide-react';
import MobilePageTopBar, { MOBILE_TOPBAR_CONTROL_H } from '../components/MobilePageTopBar';

// Custom dashboard (previous analytics behavior)
import WidgetDashboard from './components/dashboard/WidgetDashboard';
import AddWidgetButton from './components/dashboard/AddWidgetButton';
import AddWidgetPopup from './components/dashboard/AddWidgetPopup';
import { useDashboardLayout } from './utils/dashboardLayoutHooks';

import { useAnalyticsState } from './utils/analyticsStateHooks';
import { ChartFontSizeProvider } from './context/ChartFontSizeContext';
import { AnalyticsStateContext } from './context/AnalyticsStateContext';
import useCommodityParam from '../hooks/useCommodityParam';
import { useBiqPageContext } from '../context/BiqPageContext';

// Import analytics tracking
import { trackEvent, trackCommoditySelected, EVENTS } from '../utils/analytics';
import { useTerminalAnalyticsTracking } from '../hooks/useTerminalAnalyticsTracking';
import { incrementAnalyticsView, logAnalyticsLayoutUsage } from '../utils/api';

import UsageGuard from '../components/UsageGuard';
import PageSkeleton from '../components/PageSkeleton';
import { USAGE_FEATURES } from '../constants/UsageLimits';
import PresetAnalyticsStandard from './components/analytics/PresetAnalyticsStandard';
import AnalyticsChartFullscreenButton from './components/analytics/AnalyticsChartFullscreenButton';
import { getView2YtdDateRange } from '../utils/analyticsView2Ytd';

/** Scroll container for Terminal / Analytics (see DashboardLayout). */
const TERMINAL_MAIN_SCROLL_ID = 'bloom-terminal-main';

/**
 * Page padding: 24px vertical, 32px horizontal. Shared by the top filter bar
 * and the content column so the filter labels line up with the left edge of
 * the section cards below. Matches the Produce planner gutter.
 */
const PAGE_X = 'px-[16px] lg:px-[32px]';
/**
 * Surface for the pinned top filter bar (Figma). The page paints a gradient,
 * so the bar needs its own opaque fill or the dashboard would show through it
 * once it sticks. Dark values follow the panel colors used across the app.
 */
const FILTER_BAR_SURFACE =
  'bg-[#F2F6F1] dark:bg-[#191919] border-b border-[#E6E9E4] dark:border-[#3a3c3b] shadow-[0_6px_16px_rgba(0,0,0,0.06)]';
// Gap between the top filter/toolbar row and the content card below it.
// Mobile matches the Global Produce Map's 32px filter-row→card spacing
// (MapPage.jsx's `h-[32px]` spacer); desktop keeps its existing 24px
// unchanged. The pinned bar now carries part of that gap as its own
// padding-bottom (12px / 16px), so this is the remainder.
const PAGE_Y_TOP = 'max-lg:pt-[20px] lg:pt-[8px]';
const PAGE_Y_BOTTOM = 'pb-[24px]';

/** Default Terminal Analytics preset for new users (no saved `selectedView` in Firestore). */
const DEFAULT_NEW_USER_ANALYTICS_VIEW = 'view2';

/** Preset chart ids allowed for per-chart fullscreen (?chartFullscreen=…) */
const CHART_FULLSCREEN_IDS = [
  'price-line',
  'stacked-bar',
  'terminal-price-line',
  'cluster',
  // View 2 additional charts
  'volume-line',
  'fob-price-avg',
  'price-classifier',
  'cargo-varieties',
  'producing-regions',
];

/**
 * Match a commodity string against the loaded catalog (case-insensitive).
 */
const resolveCommodityInCatalog = (raw, commodities) => {
  if (!raw || !commodities) return '';
  const lower = raw.toLowerCase();
  const types = Object.keys(commodities);
  for (const type of types) {
    const typeCommodities = commodities[type];
    if (Array.isArray(typeCommodities)) {
      for (const commodity of typeCommodities) {
        if (commodity.toLowerCase() === lower) {
          return commodity.toLowerCase();
        }
      }
    }
  }
  return lower;
};

/**
 * Analytics Page - Displays customizable chart widgets with drag/resize functionality
 * 
 * Each widget fetches its own data independently using individual API hooks.
 * This page manages:
 * - Filter state (commodity, date range)
 * - Dashboard layout (widget positions and sizes)
 * - Widget add/remove operations
 */
const Analytics = () => {
  const { t } = useTranslation();
  const { setPageExtras } = useBiqPageContext() || {};
  const { email, userDetails, userDetailsLoading, firebaseUser } = useUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Mobile hamburger handler supplied by DashboardLayout via <Outlet context>.
  // Optional-chained everywhere it's used so this page never crashes if the
  // context isn't wired up yet (e.g. mid-rollout of the DashboardLayout change).
  const { onMenuClick } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  // Shared commodity source — Phase 2. Hook reads URL > Firestore-persisted
  // analytics state, and its setter writes both URL and Firestore in one call,
  // so /map and /analytics stay in sync without a separate updateState write.
  const [selectedCommodity, setSelectedCommodity] = useCommodityParam();
  const [selectedView, setSelectedView] = useState(DEFAULT_NEW_USER_ANALYTICS_VIEW);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [stagedDateStart, setStagedDateStart] = useState('');
  const [stagedDateEnd, setStagedDateEnd] = useState('');
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef(null);
  /** Preserves main column scroll while preset content is replaced by the chart fullscreen overlay. */
  const chartFullscreenScrollYRef = useRef(0);
  const wasChartFullscreenRef = useRef(false);

  const resolveDefaultDateRange = useCallback((view) => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    if (view === 'view2') {
      return getView2YtdDateRange(end);
    }
    const toYyyyMmDd = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const start = new Date(end);
    start.setDate(start.getDate() - 19);
    return { dateStart: toYyyyMmDd(start), dateEnd: toYyyyMmDd(end) };
  }, []);

  // Fetch commodities list
  const { commodities } = useCommodities();

  // Firestore: userPreferences/{email}/analytics/state (filters + widgetOptions)
  const analyticsStateApi = useAnalyticsState();
  const { state: savedAnalyticsState, isPending: statePending, updateState } = analyticsStateApi;

  const hydratedOnceRef = useRef(false);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  /** Set once when Firestore hydrate completes — used for BQ `is_default_hydration`. */
  const layoutHydrateMetaRef = useRef({ isDefaultHydration: false });
  const layoutPageLoadLoggedRef = useRef(false);

  // Track Terminal analytics views 
  useTerminalAnalyticsTracking();

  // Previous customizable dashboard layout hook (still persisted in Firestore).
  const {
    layout,
    isLoading: layoutLoading,
    removeWidget,
    applyWidgetSelection,
    handleLayoutChange,
  } = useDashboardLayout();

  useEffect(() => {
    hydratedOnceRef.current = false;
    setFiltersHydrated(false);
    layoutPageLoadLoggedRef.current = false;
  }, [email]);

  // One-time hydrate. Read priority: URL ?commodity (already reflected by the hook) >
  // Firestore-saved commodity (already reflected by the hook) > userDetails fallback >
  // 'apples' default. We only need to *write* a commodity when neither URL nor
  // Firestore provided one, so the URL/Firestore values are never overwritten.
  useEffect(() => {
    if (!email) {
      setFiltersHydrated(true);
      return;
    }
    if (statePending) return;
    if (!commodities) return;
    if (hydratedOnceRef.current) return;

    const fromFirestore = savedAnalyticsState.selectedCommodity?.trim();
    const lastFromUser =
      userDetails?.analytics_last_commodity || userDetails?.data?.analytics_last_commodity;
    const userStateResolved = userDetails !== undefined || !firebaseUser;

    // If the hook already has a value (URL or Firestore), leave it. Otherwise we
    // need userDetails resolved before applying the fallback chain.
    if (!selectedCommodity && !fromFirestore && !userStateResolved) return;
    if (!selectedCommodity && !fromFirestore && userDetailsLoading) return;

    if (!selectedCommodity) {
      let commodity;
      if (fromFirestore) {
        commodity =
          resolveCommodityInCatalog(savedAnalyticsState.selectedCommodity, commodities) ||
          savedAnalyticsState.selectedCommodity.toLowerCase();
      } else if (lastFromUser) {
        commodity =
          resolveCommodityInCatalog(lastFromUser, commodities) || lastFromUser.toLowerCase();
      } else {
        commodity = 'apples';
      }
      setSelectedCommodity(commodity);
    }
    layoutHydrateMetaRef.current = {
      isDefaultHydration: !savedAnalyticsState.selectedView,
    };
    if (savedAnalyticsState.selectedView) {
      // Back-compat:
      // - older UI used "custom" as a 4th option
      // - some states may have "view3" saved; treat it as custom
      setSelectedView(savedAnalyticsState.selectedView === 'view3' ? 'custom' : savedAnalyticsState.selectedView);
    } else {
      // New users (no saved view): default to Advanced and persist once so subsequent loads are deterministic.
      setSelectedView(DEFAULT_NEW_USER_ANALYTICS_VIEW);
      updateState({ selectedView: DEFAULT_NEW_USER_ANALYTICS_VIEW });
    }
    const ds = savedAnalyticsState.dateStart || '';
    const de = savedAnalyticsState.dateEnd || '';
    const hydratedView =
      savedAnalyticsState.selectedView === 'view3'
        ? 'custom'
        : savedAnalyticsState.selectedView || DEFAULT_NEW_USER_ANALYTICS_VIEW;
    if (!ds && !de) {
      const { dateStart: startStr, dateEnd: endStr } = resolveDefaultDateRange(hydratedView);
      setDateStart(startStr);
      setDateEnd(endStr);
      setStagedDateStart(startStr);
      setStagedDateEnd(endStr);
    } else {
      setDateStart(ds);
      setDateEnd(de);
      setStagedDateStart(ds);
      setStagedDateEnd(de);
    }

    hydratedOnceRef.current = true;
    setFiltersHydrated(true);
  }, [
    email,
    statePending,
    commodities,
    selectedCommodity,
    savedAnalyticsState.selectedCommodity,
    savedAnalyticsState.dateStart,
    savedAnalyticsState.dateEnd,
    userDetails,
    userDetailsLoading,
    firebaseUser,
    resolveDefaultDateRange,
  ]);

  // Default date range after Firestore hydrate — saved empty strings must not leave the calendar blank.
  useEffect(() => {
    if (!filtersHydrated || !selectedCommodity) return;
    if (dateStart || dateEnd || stagedDateStart || stagedDateEnd) return;

    const { dateStart: startStr, dateEnd: endStr } = resolveDefaultDateRange(selectedView);
    setDateStart(startStr);
    setDateEnd(endStr);
    setStagedDateStart(startStr);
    setStagedDateEnd(endStr);
  }, [
    filtersHydrated,
    selectedCommodity,
    selectedView,
    dateStart,
    dateEnd,
    stagedDateStart,
    stagedDateEnd,
    resolveDefaultDateRange,
  ]);

  // BQ telemetry: one `page_load` per visit after hydrate (non-blocking; guard avoids duplicate effects).
  useEffect(() => {
    if (!filtersHydrated || !email) return;
    if (!['view1', 'view2', 'custom'].includes(selectedView)) return;
    if (layoutPageLoadLoggedRef.current) return;
    layoutPageLoadLoggedRef.current = true;

    logAnalyticsLayoutUsage({
      email,
      analytics_view: selectedView,
      source: pathname,
      is_default_hydration: layoutHydrateMetaRef.current.isDefaultHydration,
      client_event_id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : undefined,
    });
  }, [filtersHydrated, email, selectedView, pathname]);

  // API params to pass to widgets - each widget fetches its own data
  const apiParams = useMemo(() => ({
    email,
    commodity: selectedCommodity,
    dateStart: dateStart || null,
    dateEnd: dateEnd || null,
  }), [email, selectedCommodity, dateStart, dateEnd]);

  useEffect(() => {
    if (!setPageExtras) return undefined;
    setPageExtras({
      commodity: selectedCommodity || '',
      view: selectedView || '',
      date_start: dateStart || '',
      date_end: dateEnd || '',
    });
    return () => setPageExtras(null);
  }, [setPageExtras, selectedCommodity, selectedView, dateStart, dateEnd]);

  // Handler for commodity selection with tracking. setSelectedCommodity is the
  // useCommodityParam setter — it writes URL + Firestore in one call, so the
  // previous explicit updateState({ selectedCommodity }) call is no longer
  // needed (would be a redundant double-write).
  const handleCommodityChange = (commodity) => {
    trackCommoditySelected(commodity, 'analytics');
    setSelectedCommodity(commodity);
    incrementAnalyticsView(commodity);
  };

  const handleViewChange = (nextView) => {
    // Back-compat: treat "view3" as custom.
    const normalized = nextView === 'view3' ? 'custom' : nextView;
    setSelectedView(normalized);
    updateState({ selectedView: normalized });
    if (email) {
      logAnalyticsLayoutUsage({
        email,
        analytics_view: normalized,
        interaction_trigger: 'view_change',
        source: pathname,
        is_default_hydration: false,
        client_event_id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : undefined,
      });
    }
    setViewMenuOpen(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('chartFullscreen');
        return next;
      },
      { replace: true }
    );
  };

  const isCustomView = selectedView === 'custom';

  const viewOptions = useMemo(
    () => [
      {
        value: 'view1',
        label: t('analytics.views.standard', 'Standard'),
        description: t('analytics.views.standardDesc', 'Get started quickly with the basics.'),
      },
      {
        value: 'view2',
        label: t('analytics.views.advanced', 'Advanced'),
        description: t(
          'analytics.views.advancedDesc',
          'More information including Producing Regions.',
        ),
      },
      {
        value: 'custom',
        label: t('analytics.views.custom', 'Custom'),
        description: t(
          'analytics.views.customDesc',
          'Configure your screen exactly how you want to see it.',
        ),
      },
    ],
    [t]
  );

  const selectedViewLabel = useMemo(() => {
    const found = viewOptions.find((o) => o.value === selectedView);
    return found?.label || viewOptions[0]?.label || 'View';
  }, [selectedView, viewOptions]);

  useEffect(() => {
    if (!viewMenuOpen) return;
    const onMouseDown = (e) => {
      if (!viewMenuRef.current) return;
      if (!viewMenuRef.current.contains(e.target)) {
        setViewMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setViewMenuOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [viewMenuOpen]);

  // Handler for saving widget selection from Add Widget modal (batch update)
  const handleSaveWidgets = (selectedIds) => {
    selectedIds.forEach((id) => {
      if (!layout.activeWidgets.includes(id)) {
        trackEvent(EVENTS.WIDGET_ADDED, { widget_id: id });
      }
    });
    applyWidgetSelection(selectedIds);
    setAddWidgetOpen(false);
  };

  // Handler for removing widget (from dashboard widget menu)
  const handleRemoveWidget = (widgetId) => {
    trackEvent(EVENTS.WIDGET_REMOVED, { widget_id: widgetId });
    removeWidget(widgetId);
  };

  // Staged date handlers — update staged values only, not apiParams
  const handleDateStartChange = (date) => {
    trackEvent(EVENTS.DATE_FILTER_CHANGED, { filter: 'start', value: date });
    setStagedDateStart(date);
  };

  const handleDateEndChange = (date) => {
    trackEvent(EVENTS.DATE_FILTER_CHANGED, { filter: 'end', value: date });
    setStagedDateEnd(date);
  };

  // Detect pending date changes
  const hasPendingDates = stagedDateStart !== dateStart || stagedDateEnd !== dateEnd;

  // Apply staged dates to actual state (triggers single apiParams update)
  const handleApplyDates = () => {
    setDateStart(stagedDateStart);
    setDateEnd(stagedDateEnd);
    updateState({ dateStart: stagedDateStart, dateEnd: stagedDateEnd });
  };

  const view2YtdCurrent = getView2YtdDateRange();
  const isView2AtYtd =
    selectedView === 'view2' &&
    dateStart === view2YtdCurrent.dateStart &&
    dateEnd === view2YtdCurrent.dateEnd &&
    stagedDateStart === view2YtdCurrent.dateStart &&
    stagedDateEnd === view2YtdCurrent.dateEnd;

  const handleResetView2Ytd = () => {
    if (selectedView !== 'view2') return;
    const { dateStart: yStart, dateEnd: yEnd } = getView2YtdDateRange();
    trackEvent(EVENTS.DATE_FILTER_CHANGED, { filter: 'reset_ytd', view: 'view2' });
    setDateStart(yStart);
    setDateEnd(yEnd);
    setStagedDateStart(yStart);
    setStagedDateEnd(yEnd);
    updateState({ dateStart: yStart, dateEnd: yEnd });
  };

  const showInitialFiltersLoading = email && !filtersHydrated;
  const isDashboardFullscreen = searchParams.get('fullscreen') === '1';
  const chartFullscreenRaw = searchParams.get('chartFullscreen');
  const chartFullscreenId =
    chartFullscreenRaw && CHART_FULLSCREEN_IDS.includes(chartFullscreenRaw) ? chartFullscreenRaw : null;
  const isChartFullscreen = !!chartFullscreenId && !isCustomView;

  const handleEnterDashboardFullscreen = () => {
    const next = new URLSearchParams(searchParams);
    next.set('fullscreen', '1');
    next.delete('chartFullscreen');
    setSearchParams(next, { replace: true });
  };

  const handleExitDashboardFullscreen = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('fullscreen');
    setSearchParams(next, { replace: true });
  };

  const handleEnterChartFullscreen = (chartId) => {
    if (!CHART_FULLSCREEN_IDS.includes(chartId)) return;
    const mainEl = document.getElementById(TERMINAL_MAIN_SCROLL_ID);
    if (mainEl) {
      chartFullscreenScrollYRef.current = mainEl.scrollTop;
    }
    const next = new URLSearchParams(searchParams);
    next.set('chartFullscreen', chartId);
    next.delete('fullscreen');
    setSearchParams(next, { replace: true });
  };

  const handleExitChartFullscreen = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('chartFullscreen');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!isDashboardFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleExitDashboardFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDashboardFullscreen]);

  // Escape leaves single-chart fullscreen (chart UI also has Exit).
  useEffect(() => {
    if (!isChartFullscreen) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('chartFullscreen');
          return next;
        },
        { replace: true }
      );
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isChartFullscreen, setSearchParams]);

  useEffect(() => {
    if (!isChartFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isChartFullscreen]);

  // After exiting chart fullscreen, restore scroll on the Terminal main column (overlay shrinks content height).
  useLayoutEffect(() => {
    if (wasChartFullscreenRef.current && !isChartFullscreen) {
      const mainEl = document.getElementById(TERMINAL_MAIN_SCROLL_ID);
      if (mainEl) {
        mainEl.scrollTop = chartFullscreenScrollYRef.current;
      }
    }
    wasChartFullscreenRef.current = isChartFullscreen;
  }, [isChartFullscreen]);

  // Drop unknown chartFullscreen query values.
  useEffect(() => {
    const raw = searchParams.get('chartFullscreen');
    if (!raw || CHART_FULLSCREEN_IDS.includes(raw)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('chartFullscreen');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  // Single-chart fullscreen only applies to preset views.
  useEffect(() => {
    if (!isCustomView || !searchParams.get('chartFullscreen')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('chartFullscreen');
        return next;
      },
      { replace: true }
    );
  }, [isCustomView, searchParams, setSearchParams]);

  return (
    <UsageGuard
      feature={USAGE_FEATURES.ANALYTICS_VIEW}
      featureName="Analytics"
      trackOnMount={false}
    >
      <AnalyticsStateContext.Provider value={analyticsStateApi}>
      <ChartFontSizeProvider>
        <div className="w-full min-h-full bg-[#F0F5F2] bg-dark-app">
        {isChartFullscreen ? (
          <div className="fixed inset-0 z-[100] flex flex-col bg-[#F0F5F2] bg-dark-app">
            {/* Below lg this is the single scroll container for the chart: the
                column no longer fits the viewport once the KPI cards and legend
                stack, so it scrolls instead of squeezing the plot to 0px
                (`.preset-fullscreen-scroll`, index.css). At lg+ the column still
                fills the viewport exactly and stays clipped. */}
            <div className="preset-fullscreen-scroll flex-1 min-h-0 overflow-hidden flex flex-col">
              {showInitialFiltersLoading ? (
                <div className="flex flex-1 min-h-0 items-center justify-center">
                  <p className="text-[#828382] dark:text-gray-400">{t('analytics.loadingLayout')}</p>
                </div>
              ) : !selectedCommodity ? (
                <div className="flex flex-1 min-h-0 items-center justify-center">
                  <p className="text-[#828382] dark:text-gray-400 text-lg">{t('analytics.selectCommodity')}</p>
                </div>
              ) : (
                <PresetAnalyticsStandard
                  apiParams={apiParams}
                  chartFullscreenId={chartFullscreenId}
                  presetView={selectedView}
                  onEnterChartFullscreen={handleEnterChartFullscreen}
                  onExitChartFullscreen={handleExitChartFullscreen}
                  isDashboardFullscreen={false}
                />
              )}
            </div>
          </div>
        ) : (
          <>
        <MobilePageTopBar onMenuClick={onMenuClick} isFullscreen={isDashboardFullscreen} onEnterFullscreen={handleEnterDashboardFullscreen} onExitFullscreen={handleExitDashboardFullscreen} withContentGap={false}>
          <div className="relative z-[60] w-fit max-w-full min-w-[180px]">
            <NestedCommoditySelector variant="stacked" heightClass={MOBILE_TOPBAR_CONTROL_H} value={selectedCommodity} onChange={handleCommodityChange} placeholder={t('analytics.select')} />
          </div>
        </MobilePageTopBar>
        {/* Top filter bar — pinned to the top of the Terminal scroll container
            (`#bloom-terminal-main`) so the filters stay reachable while the
            dashboard scrolls. Sticky also makes it a positioned stacking
            context, so the open dropdowns paint above the section cards below. */}
        <div
          className={`sticky top-0 z-50 ${PAGE_X} ${FILTER_BAR_SURFACE} max-lg:pt-[8px] max-lg:pb-[12px] lg:pt-[24px] lg:pb-[16px]`}
        >
          <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            {/* Left controls */}
            <div className="flex flex-wrap items-end justify-start gap-3 lg:gap-x-5 lg:gap-y-3">
              {isDashboardFullscreen ? (
                <Link
                  to="/overview"
                  className="flex items-center"
                  aria-label={t('nav.home', 'Home')}
                >
                  <img
                    src="/assets/logo.png"
                    alt=""
                    className="h-9 w-[28px] sm:h-10 sm:w-[32px] object-contain"
                  />
                </Link>
              ) : null}

              <div className="min-w-[136px]">
                <DateDropdown
                  variant="stacked"
                  value={stagedDateStart}
                  onChange={handleDateStartChange}
                  label={t('analytics.from')}
                  placeholder={t('analytics.select')}
                />
              </div>

              <div className="min-w-[136px]">
                <DateDropdown
                  variant="stacked"
                  value={stagedDateEnd}
                  onChange={handleDateEndChange}
                  label={t('analytics.to')}
                  placeholder={t('analytics.select')}
                />
              </div>

              {hasPendingDates ? (
                <button
                  type="button"
                  onClick={handleApplyDates}
                  className="flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-lg border border-bloom-green bg-bloom-green px-4 text-sm font-semibold text-white transition-colors hover:bg-bloom-green-hover"
                >
                  {t('analytics.applyDates', 'Apply')}
                </button>
              ) : null}

              <div className="hidden min-w-[180px] lg:block">
                <NestedCommoditySelector
                  variant="stacked"
                  value={selectedCommodity}
                  onChange={handleCommodityChange}
                  placeholder={t('analytics.select')}
                  label={t('analytics.commodity')}
                />
              </div>

              <div className="min-w-[136px]">
                <label className={FILTER_LABEL_CLASS}>{t('analytics.view', 'View')}</label>
                <div ref={viewMenuRef} className="relative w-full min-w-0">
                  <button
                    type="button"
                    onClick={() => setViewMenuOpen((v) => !v)}
                    className={`w-full min-w-0 outline-none ${FILTER_VALUE_TEXT_CLASS} ${filterTriggerClass({ open: viewMenuOpen })}`}
                    aria-haspopup="listbox"
                    aria-expanded={viewMenuOpen}
                  >
                    <span className={FILTER_VALUE_CLASS}>{selectedViewLabel}</span>
                    <ChevronDown
                      size={FILTER_CHEVRON_SIZE}
                      className={`${FILTER_CHEVRON_CLASS} ${viewMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {viewMenuOpen && (
                    <div
                      role="listbox"
                      className="absolute top-full left-0 mt-2 w-full rounded-lg border border-[#E2E2E2] dark:border-[#3a3c3b] bg-white dark:bg-[#282a29] shadow-lg z-50"
                    >
                      {viewOptions.map((opt, idx) => {
                        const active = opt.value === selectedView;
                        const tooltipId = `view-tooltip-${opt.value}`;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={active}
                            aria-describedby={opt.description ? tooltipId : undefined}
                            onClick={() => handleViewChange(opt.value)}
                            className={
                              'group relative w-full px-3 py-2 text-left text-sm transition-colors cursor-pointer ' +
                              (idx === 0 ? 'rounded-t-lg ' : '') +
                              (idx === viewOptions.length - 1 ? 'rounded-b-lg ' : '') +
                              (active
                                ? 'bg-bloom-green/10 text-[#282A29] dark:text-white'
                                : 'text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#E8EBE9] dark:hover:bg-[#333534]')
                            }
                          >
                            {opt.label}
                            {opt.description ? (
                              <span
                                id={tooltipId}
                                role="tooltip"
                                className="pointer-events-none invisible group-hover:visible absolute left-full top-1/2 -translate-y-1/2 ml-2 w-56 px-3 py-2 rounded-md bg-[#282A29] text-white text-xs leading-snug shadow-lg z-[60] whitespace-normal"
                              >
                                {opt.description}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {selectedView === 'view2' && selectedCommodity && !isView2AtYtd ? (
                <button
                  type="button"
                  onClick={handleResetView2Ytd}
                  aria-label={t('analytics.resetToYtdAria')}
                  className="flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-[#C3C4C3] bg-[#F4F5F4] px-3 text-sm font-semibold text-[#282A29] transition-colors hover:border-bloom-green/70 hover:bg-white dark:border-[#5d625f] dark:bg-[#333534] dark:text-[#f0f5f2] dark:hover:border-bloom-green/60 dark:hover:bg-[#3a3c3b]"
                >
                  <RotateCcw size={16} className="shrink-0 text-[#444746] dark:text-[#e8ebe9]" aria-hidden />
                  {t('analytics.resetToYtd', 'Reset to YTD')}
                </button>
              ) : null}

              {/* Mobile-only BiQ report — wraps onto the same row as the View
                  dropdown. Desktop keeps its copy in the right-actions group
                  below (hidden lg:flex there / this one lg:hidden). */}
              <button
                type="button"
                disabled={!selectedCommodity}
                onClick={() => {
                  if (!selectedCommodity) return;
                  navigate('/biq', { state: { generateReport: true, commodity: selectedCommodity } });
                }}
                className="flex lg:hidden h-[32px] self-end items-center gap-2 rounded-lg border border-[#E2E2E2] dark:border-[#3a3c3b] bg-white dark:bg-[#191919] px-3 text-[12px] font-medium text-[#282A29] dark:text-white hover:border-bloom-green/60 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon icon="lsicon:report-filled" width="16" height="16" className="text-bloom-green" />
                {t('analytics.biqAdvancedReport', 'BiQ enhanced report')}
              </button>
            </div>

            {/* Right actions */}
            <div className="flex flex-wrap items-center gap-3 max-lg:justify-start lg:justify-end">
              {isCustomView && (
                <AddWidgetButton
                  onClick={() => setAddWidgetOpen(true)}
                  disabled={!selectedCommodity}
                />
              )}

              <button
                type="button"
                disabled={!selectedCommodity}
                onClick={() => {
                  if (!selectedCommodity) return;
                  navigate('/biq', {
                    state: {
                      generateReport: true,
                      commodity: selectedCommodity,
                    },
                  });
                }}
                className="hidden lg:flex h-10 px-4 rounded-lg border border-[#E2E2E2] dark:border-[#3a3c3b] bg-white dark:bg-[#191919] text-[#282A29] dark:text-white text-sm font-medium hover:border-bloom-green/60 transition-colors items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[#E2E2E2] dark:disabled:hover:border-[#3a3c3b]"
              >
                <Icon icon="lsicon:report-filled" width="16" height="16" className="text-bloom-green" />
                {t('analytics.biqAdvancedReport', 'BiQ enhanced report')}
              </button>

              {/* Hidden on mobile — the mobile-only top row above already
                  renders this same control, top-right, next to the
                  hamburger. Desktop (lg+) keeps it here, unchanged. */}
              <div className="hidden lg:block">
                <AnalyticsChartFullscreenButton
                  compact
                  isActive={isDashboardFullscreen}
                  onEnter={handleEnterDashboardFullscreen}
                  onExit={handleExitDashboardFullscreen}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Preset Dashboard (locked) */}
        <div
          className={
            isDashboardFullscreen
              ? `w-full px-0 ${PAGE_Y_TOP}`
              : `w-full ${PAGE_X} ${PAGE_Y_TOP} ${PAGE_Y_BOTTOM}`
          }
        >
          {showInitialFiltersLoading ? (
            <div className="flex w-full flex-col gap-4">
              <PageSkeleton shape="hero" />
              <PageSkeleton shape="chart" />
            </div>
          ) : !selectedCommodity ? (
            <div className="flex items-center justify-center h-[400px]">
              <p className="text-[#828382] dark:text-gray-400 text-lg">{t('analytics.selectCommodity')}</p>
            </div>
          ) : isCustomView ? (
            layoutLoading ? (
              <PageSkeleton shape="chart" />
            ) : (
              <WidgetDashboard
                layout={layout}
                apiParams={apiParams}
                onLayoutChange={handleLayoutChange}
                onRemoveWidget={handleRemoveWidget}
              />
            )
          ) : (
            <PresetAnalyticsStandard
              apiParams={apiParams}
              isDashboardFullscreen={isDashboardFullscreen}
              presetView={selectedView}
              onEnterChartFullscreen={handleEnterChartFullscreen}
              onExitChartFullscreen={handleExitChartFullscreen}
            />
          )}
        </div>

        {isCustomView && (
          <AddWidgetPopup
            isOpen={addWidgetOpen}
            onClose={() => setAddWidgetOpen(false)}
            onSave={handleSaveWidgets}
            activeWidgetIds={layout.activeWidgets}
          />
        )}
          </>
        )}
        </div>
      </ChartFontSizeProvider>
      </AnalyticsStateContext.Provider>
    </UsageGuard>
  );
};

export default Analytics;
